// @ts-nocheck
import { useState, useEffect, useRef } from "react";
import { useRoute, useLocation } from "wouter";

const API_BASE = import.meta.env.VITE_API_URL ?? "";

export function DeviceQR() {
  const [, params] = useRoute("/devices/:id/qr");
  const [, navigate] = useLocation();
  const deviceId = params?.id ? Number(params.id) : null;

  const [qrDataUrl, setQrDataUrl] = useState<string>("");
  const [status, setStatus] = useState<"loading" | "scanning" | "connected" | "error">("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const [deviceName, setDeviceName] = useState("Device");
  const [connectedMsg, setConnectedMsg] = useState("");
  const [qrAge, setQrAge] = useState(0); // seconds since last QR

  const sessionStarted = useRef(false);
  const qrPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const statusPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const qrAgeRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function clearAll() {
    if (qrPollRef.current) clearInterval(qrPollRef.current);
    if (statusPollRef.current) clearInterval(statusPollRef.current);
    if (qrAgeRef.current) clearInterval(qrAgeRef.current);
  }

  // Poll GET /devices/:id/status
  async function pollStatus() {
    if (!deviceId) return;
    try {
      const res = await fetch(`${API_BASE}/api/devices/${deviceId}/status`, { cache: "no-store" });
      const data = await res.json();
      if (data.connected === true) {
        clearAll();
        setStatus("connected");
        setConnectedMsg(data.message ?? "Device Connected!");
        await fetch(`${API_BASE}/api/devices/${deviceId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "connected" }),
        });
      }
    } catch {}
  }

  // Poll GET /devices/:id/qr to refresh the displayed QR
  async function pollQr() {
    if (!deviceId) return;
    try {
      const res = await fetch(`${API_BASE}/api/devices/${deviceId}/qr`, { cache: "no-store" });
      const data = await res.json();
      if (data.status === "connected") {
        clearAll();
        setStatus("connected");
        return;
      }
      if (data.qrcode) {
        setQrDataUrl(data.qrcode);
        setQrAge(0);
        if (status !== "scanning") setStatus("scanning");
      }
    } catch {}
  }

  // Start session ONCE — then rely on polling for QR/status updates
  async function startSession() {
    if (!deviceId || sessionStarted.current) return;
    sessionStarted.current = true;
    try {
      const res = await fetch(`${API_BASE}/api/devices/${deviceId}/session`, {
        method: "POST",
        cache: "no-store",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setStatus("error");
        setErrorMsg(err.error ?? "Failed to start session");
        return;
      }
      const data = await res.json();
      if (data.status === "connected") {
        clearAll();
        setStatus("connected");
        return;
      }
      if (data.qrcode) {
        setQrDataUrl(data.qrcode);
        setQrAge(0);
        setStatus("scanning");
      }
      // Start polling QR every 8 seconds (just to refresh display; session stays alive)
      qrPollRef.current = setInterval(pollQr, 8000);
    } catch {
      setStatus("error");
      setErrorMsg("Could not connect to server");
    }
  }

  useEffect(() => {
    if (!deviceId) return;

    fetch(`${API_BASE}/api/devices/${deviceId}`)
      .then(r => r.json())
      .then(d => { if (d.name) setDeviceName(d.name); })
      .catch(() => {});

    startSession();

    // Poll status every 3 seconds
    statusPollRef.current = setInterval(pollStatus, 3000);

    // Age counter
    qrAgeRef.current = setInterval(() => setQrAge(a => a + 1), 1000);

    return () => clearAll();
  }, [deviceId]);

  async function handleLogout() {
    if (!deviceId) return;
    if (!confirm("Are you sure you want to logout?")) return;
    clearAll();
    await fetch(`${API_BASE}/api/devices/${deviceId}/session`, { method: "DELETE" });
    navigate("/devices");
  }

  function handleRetry() {
    sessionStarted.current = false;
    setStatus("loading");
    setErrorMsg("");
    setQrDataUrl("");
    setQrAge(0);
    clearAll();
    statusPollRef.current = setInterval(pollStatus, 3000);
    qrAgeRef.current = setInterval(() => setQrAge(a => a + 1), 1000);
    startSession();
  }

  const externalUrl = `${window.location.origin}/wasender/devices/${deviceId}/qr`;

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <button
          onClick={() => navigate("/devices")}
          style={{ background: "white", border: "1px solid #e9ecef", borderRadius: 8, padding: "8px 16px", cursor: "pointer", color: "#525f7f", fontSize: 14 }}
        >
          <i className="fas fa-arrow-left" style={{ marginRight: 6 }} />
          Back
        </button>
        <div style={{ flex: 1 }}>
          <h4 style={{ margin: 0, color: "#32325d", fontSize: 20, fontWeight: 600 }}>Scan QR Code</h4>
          <p style={{ margin: 0, color: "#8898aa", fontSize: 13 }}>{deviceName}</p>
        </div>
        <a
          href={externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#f6f9fc", border: "1px solid #e9ecef", borderRadius: 8, padding: "8px 16px", cursor: "pointer", color: "#1171ef", fontSize: 13, fontWeight: 600, textDecoration: "none" }}
        >
          <i className="fas fa-external-link-alt" />
          Open in new tab
        </a>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 24, maxWidth: 900 }}>
        <div style={{ background: "white", borderRadius: 12, boxShadow: "0 0 2rem 0 rgba(136,152,170,.15)", overflow: "hidden" }}>
          <div style={{ padding: "16px 24px", borderBottom: "1px solid #e9ecef", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h5 style={{ margin: 0, color: "#32325d", fontSize: 16 }}>Scan the QR Code On Your WhatsApp Mobile App</h5>
            {status === "connected" && (
              <button onClick={handleLogout} style={{ background: "white", border: "1px solid #e9ecef", borderRadius: 8, padding: "6px 14px", cursor: "pointer", color: "#525f7f", fontSize: 13 }}>
                <i className="fas fa-sign-out-alt" style={{ marginRight: 6 }} />Logout
              </button>
            )}
          </div>
          <div style={{ padding: 32 }}>
            <div style={{ display: "flex", justifyContent: "center", minHeight: 300, alignItems: "center" }}>
              {status === "loading" && (
                <div style={{ textAlign: "center" }}>
                  <div style={{ width: 40, height: 40, border: "4px solid #1171ef", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 16px" }} />
                  <p style={{ color: "#525f7f", fontWeight: 600 }}>Generating QR code...</p>
                  <p style={{ color: "#8898aa", fontSize: 13 }}>Connecting to WhatsApp servers...</p>
                </div>
              )}
              {status === "scanning" && qrDataUrl && (
                <div style={{ textAlign: "center" }}>
                  <a href={qrDataUrl} target="_blank" rel="noopener noreferrer" title="Click to open QR full-screen">
                    <img
                      src={qrDataUrl}
                      alt="WhatsApp QR Code"
                      style={{ width: 320, height: 320, borderRadius: 12, border: "3px solid #e9ecef", display: "block", cursor: "zoom-in" }}
                    />
                  </a>
                  <p style={{ color: "#8898aa", fontSize: 12, marginTop: 10 }}>
                    {qrAge < 55 ? `QR refreshes in ${55 - qrAge}s` : "QR code refreshing..."}
                  </p>
                  <a href={qrDataUrl} target="_blank" rel="noopener noreferrer"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 13, color: "#1171ef", fontWeight: 600, textDecoration: "none" }}>
                    <i className="fas fa-expand-alt" />
                    Open full screen for easier scanning
                  </a>
                </div>
              )}
              {status === "connected" && (
                <div style={{ textAlign: "center" }}>
                  <div style={{ width: 80, height: 80, borderRadius: "50%", background: "#d4edda", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                    <i className="fas fa-check" style={{ color: "#2dce89", fontSize: 36 }} />
                  </div>
                  <h5 style={{ color: "#32325d", marginBottom: 8 }}>{connectedMsg || "Device Connected!"}</h5>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 24 }}>
                    <a href="/single-send" onClick={e => { e.preventDefault(); navigate("/single-send"); }} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", background: "white", border: "1px solid #e9ecef", borderRadius: 8, textDecoration: "none", color: "#525f7f", fontSize: 13 }}>
                      <i className="fas fa-paper-plane" style={{ color: "#1171ef" }} />Send a Message
                    </a>
                    <a href="/campaigns" onClick={e => { e.preventDefault(); navigate("/campaigns"); }} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", background: "white", border: "1px solid #e9ecef", borderRadius: 8, textDecoration: "none", color: "#525f7f", fontSize: 13 }}>
                      <i className="fas fa-rocket" style={{ color: "#1171ef" }} />Send Bulk Message
                    </a>
                  </div>
                </div>
              )}
              {status === "error" && (
                <div style={{ textAlign: "center" }}>
                  <div style={{ width: 80, height: 80, borderRadius: "50%", background: "#fde8ec", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                    <i className="fas fa-times" style={{ color: "#f5365c", fontSize: 36 }} />
                  </div>
                  <h5 style={{ color: "#32325d", marginBottom: 8 }}>Connection Failed</h5>
                  <p style={{ color: "#8898aa", fontSize: 13, marginBottom: 16 }}>{errorMsg}</p>
                  <button onClick={handleRetry} style={{ background: "linear-gradient(87deg,#11cdef,#1171ef)", color: "white", border: "none", borderRadius: 8, padding: "10px 24px", cursor: "pointer", fontWeight: 600 }}>
                    <i className="fas fa-sync-alt" style={{ marginRight: 8 }} />Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
          <div style={{ padding: "16px 24px", borderTop: "1px solid #e9ecef" }}>
            {status === "connected" && (
              <div style={{ background: "#d4edda", color: "#155724", padding: "12px 16px", borderRadius: 8, fontSize: 13 }}>
                <i className="fas fa-check-circle" style={{ marginRight: 8 }} />Device Connected Successfully!
              </div>
            )}
            {status === "error" && (
              <div style={{ background: "#f8d7da", color: "#721c24", padding: "12px 16px", borderRadius: 8, fontSize: 13 }}>
                <i className="fas fa-exclamation-circle" style={{ marginRight: 8 }} />Server disconnected. Please try again.
              </div>
            )}
            {(status === "loading" || status === "scanning") && (
              <div style={{ height: 48 }} />
            )}
          </div>
        </div>

        <div style={{ background: "white", borderRadius: 12, boxShadow: "0 0 2rem 0 rgba(136,152,170,.15)", overflow: "hidden" }}>
          <div style={{ padding: "16px 24px", borderBottom: "1px solid #e9ecef" }}>
            <h5 style={{ margin: 0, color: "#32325d", fontSize: 16 }}>How To Scan?</h5>
          </div>
          <div style={{ padding: "0 24px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {[
                { icon: "ni ni-mobile-button", step: "Step 1", text: "Open WhatsApp on your phone" },
                { icon: "ni ni-active-40", step: "Step 2", text: "Tap Menu or Settings and select Linked Devices" },
                { icon: "ni ni-active-40", step: "Step 3", text: "Tap on Link a Device" },
                { icon: "fas fa-qrcode", step: "Step 4", text: "Point your phone to this screen to capture the code" },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", gap: 16, padding: "20px 0", borderBottom: i < 3 ? "1px solid #f6f9fc" : "none" }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: "linear-gradient(87deg,#11cdef,#1171ef)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <i className={item.icon} style={{ color: "white", fontSize: 16 }} />
                  </div>
                  <div>
                    <p style={{ margin: "0 0 4px", color: "#1171ef", fontSize: 12, fontWeight: 600 }}>{item.step}</p>
                    <p style={{ margin: 0, color: "#525f7f", fontSize: 13 }}>{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
